import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { Button } from '~/components/Button';
import { ErrorToast } from '~/components/ErrorToast/ErrorToast';
import { cn } from '~/lib/cn';
import { ArtistExportDialog } from '~/modules/artist-roster/components/ArtistExport/ArtistExportDialog';
import { useIsContactInfoEnabled } from '~/modules/artist-roster/hooks/useIsContactInfoEnabled';
import { exportArtistsCSV } from '~/modules/artist-roster/lib/artists';
import { ERROR_FORM_LINK } from '~/modules/artist-roster/lib/constants';
import { getRosterStatus } from '~/modules/artist-roster/lib/roster';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

interface ArtistExportProps {
  search?: string;
  labels?: number[];
  isCrossover?: boolean;
  isOnRoster?: boolean;
  className?: string;
}

function ArtistExport({
  search = '',
  className = '',
  isCrossover = false,
  isOnRoster = undefined,
  labels = [],
}: ArtistExportProps) {
  const { t } = useTranslation('roster');
  const [isExporting, setIsExporting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isErrorToastOpen, setIsErrorToastOpen] = useState(false);
  const { trackEvent } = useAnalytics('Artist Roster');
  const { enabled: isContactDetailsEnabled } = useIsContactInfoEnabled();

  const exportCSV = async (withContactInfo: boolean) => {
    setIsExporting(true);
    setIsDialogOpen(false);

    try {
      const isExportingComplete = await exportArtistsCSV({
        search,
        labels,
        isCrossover,
        isOnRoster,
        ...(withContactInfo ? { withContactInfo } : {}),
      });

      if (isExportingComplete) {
        trackEvent('rosterExported', {
          roster_label_filters_count: labels.length,
          roster_label_filters_applied: labels.length > 0,
          roster_crossover_filter_applied: isCrossover,
          roster_status: getRosterStatus(isOnRoster),
        });
      }
    } catch (error) {
      setIsErrorToastOpen(true);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportButtonClick = async () => {
    if (isContactDetailsEnabled) {
      setIsDialogOpen(true);

      return;
    }

    await exportCSV(false);
  };

  const handleDialogChange = (state: boolean) => {
    setIsDialogOpen(state);
  };

  return (
    <div>
      <Button
        variant="text"
        className={cn(
          'flex items-center justify-center gap-sm font-semibold',
          className,
        )}
        onClick={handleExportButtonClick}
        disabled={isExporting}
      >
        {isExporting ? t('export.labels.exporting') : t('export.labels.export')}
        <div className="inline-block">
          <Icon type="file-export" variant="regular" size="sm" />
        </div>
        <ArtistExportDialog
          isOpen={isDialogOpen}
          onExportClick={exportCSV}
          onChange={handleDialogChange}
        />
      </Button>
      <ErrorToast
        isOpen={isErrorToastOpen}
        onClose={() => setIsErrorToastOpen(false)}
        title={t('export.error.title')}
        description={
          <Trans
            t={t}
            i18nKey="export.error.caption"
            components={{
              a: (
                <a
                  href={ERROR_FORM_LINK}
                  className="underline"
                  target="_blank"
                  aria-label={t('export.error.aria')}
                  rel="noreferrer"
                />
              ),
            }}
          />
        }
      />
    </div>
  );
}

ArtistExport.displayName = 'ArtistExport';

export { ArtistExport };
